import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './dialog';
import { Button } from './button';
import { Input } from './input';
import { Label } from './label';
import { Checkbox } from './checkbox';
import { Alert, AlertDescription } from './alert';
import { AlertTriangle, Shield, DollarSign, ArrowRight } from 'lucide-react';
import { ReembolsoService } from '@/services/reembolsoService';

interface ModalReembolsoPixProps {
  isOpen: boolean;
  onClose: () => void;
  saldoDisponivel: number;
  cpfUsuario: string; // CPF do usuário logado
  onSuccess: () => void;
  userId: string;
}

const ModalReembolsoPix: React.FC<ModalReembolsoPixProps> = ({
  isOpen,
  onClose,
  saldoDisponivel,
  cpfUsuario,
  onSuccess,
  userId
}) => {
  const [valor, setValor] = useState('');
  const [motivo, setMotivo] = useState('');
  const [cpfChavePix, setCpfChavePix] = useState('');
  const [confirmacaoChecked, setConfirmacaoChecked] = useState(false);
  const [avisoChecked, setAvisoChecked] = useState(false);
  const [loading, setLoading] = useState(false);
  const [erros, setErros] = useState<string[]>([]);

  const validarFormulario = (): string[] => {
    const novosErros: string[] = [];

    // Validar valor
    const valorNum = parseFloat(valor);
    if (!valor || valorNum <= 0) {
      novosErros.push('Valor deve ser maior que zero');
    }
    if (valorNum > saldoDisponivel) {
      novosErros.push(`Valor não pode ser maior que o saldo disponível (${ReembolsoService.formatarValor(saldoDisponivel)})`);
    }
    if (valorNum < 10) {
      novosErros.push('Valor mínimo para reembolso é R$ 10,00');
    }

    // Validar CPF PRIMEIRO
    if (!cpfChavePix.trim()) {
      novosErros.push('CPF da chave PIX é obrigatório');
    }
    
    // Limpar CPFs para comparação
    const cpfLimpo = cpfChavePix.replace(/\D/g, '');
    const cpfUserLimpo = cpfUsuario.replace(/\D/g, '');
    
    // Validar formato do CPF (deve ter exatamente 11 dígitos)
    if (cpfLimpo.length !== 11) {
      novosErros.push('CPF deve ter exatamente 11 dígitos');
    } else {
      // Validar CPF apenas se tem 11 dígitos
      if (!ReembolsoService.validarCPF(cpfChavePix)) {
        novosErros.push('CPF inválido');
      }
    }

    // Validar se CPF confere com o usuário (comparar sempre os CPFs limpos)
    if (cpfLimpo && cpfUserLimpo && cpfLimpo !== cpfUserLimpo) {
      novosErros.push('CPF da chave PIX deve ser o mesmo CPF cadastrado na sua conta');
    }

    // ✅ NOVA LÓGICA: Se CPF é válido e igual ao cadastrado, motivo é opcional
    const cpfValido = cpfLimpo.length === 11 && 
                     ReembolsoService.validarCPF(cpfChavePix) && 
                     cpfLimpo === cpfUserLimpo;

    // Validar motivo apenas se CPF não for válido ou se motivo foi preenchido
    if (!cpfValido) {
      // Se CPF não é válido, motivo é obrigatório
      if (!motivo.trim()) {
        novosErros.push('Motivo é obrigatório quando CPF não confere com o cadastrado');
      }
      if (motivo.length > 0 && motivo.length < 10) {
        novosErros.push('Motivo deve ter pelo menos 10 caracteres');
      }
    } else {
      // Se CPF é válido, motivo é opcional, mas se preenchido deve ter pelo menos 10 caracteres
      if (motivo.length > 0 && motivo.length < 10) {
        novosErros.push('Motivo deve ter pelo menos 10 caracteres');
      }
    }
    
    // Debug: mostrar CPFs para comparação
    

    // Validar confirmações
    if (!confirmacaoChecked) {
      novosErros.push('Você deve confirmar que o CPF é sua chave PIX');
    }
    if (!avisoChecked) {
      novosErros.push('Você deve aceitar os termos de reembolso');
    }

    return novosErros;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const novosErros = validarFormulario();
    setErros(novosErros);
    
    if (novosErros.length > 0) return;

    try {
      setLoading(true);
      
      await ReembolsoService.criarReembolso({
        user_id: userId,
        valor: parseFloat(valor),
        motivo,
        cpf_chave_pix: cpfChavePix
      });

      onSuccess();
      handleClose();
    } catch (error: unknown) {
      const err = error as Error;
      setErros([err.message || 'Erro ao solicitar reembolso']);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setValor('');
    setMotivo('');
    setCpfChavePix('');
    setConfirmacaoChecked(false);
    setAvisoChecked(false);
    setErros([]);
    onClose();
  };

  const formatarValorInput = (value: string) => {
    const numero = value.replace(/\D/g, '');
    const valorFormatado = (parseFloat(numero) / 100).toFixed(2);
    return valorFormatado;
  };

  const formatarCpfInput = (value: string) => {
    const numero = value.replace(/\D/g, '');
    
    // Limitar a 11 dígitos
    const numeroLimitado = numero.slice(0, 11);
    
    // Aplicar formatação progressiva
    if (numeroLimitado.length <= 3) {
      return numeroLimitado;
    } else if (numeroLimitado.length <= 6) {
      return numeroLimitado.replace(/(\d{3})(\d{0,3})/, '$1.$2');
    } else if (numeroLimitado.length <= 9) {
      return numeroLimitado.replace(/(\d{3})(\d{3})(\d{0,3})/, '$1.$2.$3');
    } else {
      return numeroLimitado.replace(/(\d{3})(\d{3})(\d{3})(\d{0,2})/, '$1.$2.$3-$4');
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[95vh] overflow-y-auto sm:max-h-[90vh] p-4 sm:p-6">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <DollarSign className="w-6 h-6 text-green-600" />
            Solicitar Reembolso PIX
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Info do Saldo */}
          <div className="bg-blue-50 p-4 rounded-lg">
            <p className="text-sm text-blue-800">
              <strong>Saldo Disponível:</strong> {ReembolsoService.formatarValor(saldoDisponivel)}
            </p>
          </div>

          {/* Alerta de Segurança */}
          <Alert className="border-orange-200 bg-orange-50">
            <Shield className="w-4 h-4 text-orange-600" />
            <AlertDescription className="text-orange-800">
              <strong>IMPORTANTE:</strong> O reembolso será feito apenas na chave PIX do seu CPF cadastrado. 
              Não realizamos reembolsos para contas de terceiros para sua segurança.
            </AlertDescription>
          </Alert>

          {/* Valor */}
          <div className="space-y-2">
            <Label htmlFor="valor">Valor do Reembolso (R$)</Label>
            <Input
              id="valor"
              type="number"
              step="0.01"
              min="10"
              max={saldoDisponivel}
              value={valor}
              onChange={(e) => setValor(e.target.value)}
              placeholder="0,00"
              className="text-lg"
            />
            <p className="text-xs text-gray-500">
              Valor mínimo: R$ 10,00 | Máximo: {ReembolsoService.formatarValor(saldoDisponivel)}
            </p>
          </div>

          {/* Motivo */}
          <div className="space-y-2">
            <Label htmlFor="motivo">Motivo do Reembolso</Label>
            <textarea
              id="motivo"
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
              placeholder="Explique o motivo da solicitação do reembolso."
              className="w-full p-3 border border-gray-300 rounded-md resize-none h-20"
              maxLength={200}
            />
            <p className="text-xs text-gray-500">
              {motivo.length}/200 caracteres (mínimo 10 se preenchido)
            </p>
          </div>

          {/* CPF Chave PIX */}
          <div className="space-y-2">
            <Label htmlFor="cpf">CPF da Chave PIX</Label>
            <Input
              id="cpf"
              type="text"
              value={cpfChavePix}
              onChange={(e) => setCpfChavePix(formatarCpfInput(e.target.value))}
              placeholder="000.000.000-00"
              maxLength={14}
              className="font-mono"
            />
            <p className="text-xs text-gray-500">
              Deve ser o mesmo CPF cadastrado na sua conta: {ReembolsoService.formatarCPF(cpfUsuario)}
            </p>
          </div>

          {/* Confirmações */}
          <div className="space-y-4">
            <div className="flex items-start space-x-2">
              <Checkbox
                id="confirmacao"
                checked={confirmacaoChecked}
                onCheckedChange={(checked) => setConfirmacaoChecked(checked === true)}
              />
              <Label htmlFor="confirmacao" className="text-sm">
                Confirmo que o CPF informado é minha chave PIX e a conta bancária está em meu nome.
              </Label>
            </div>

            <div className="flex items-start space-x-2">
              <Checkbox
                id="aviso"
                checked={avisoChecked}
                onCheckedChange={(checked) => setAvisoChecked(checked === true)}
              />
              <Label htmlFor="aviso" className="text-sm">
                Entendo que após confirmar esta solicitação, o valor será <strong>imediatamente descontado</strong> 
                do meu saldo e não poderei cancelar o pedido.
              </Label>
            </div>
          </div>

          {/* Erros */}
          {erros.length > 0 && (
            <Alert className="border-red-200 bg-red-50">
              <AlertTriangle className="w-4 h-4 text-red-600" />
              <AlertDescription className="text-red-800">
                <ul className="list-disc list-inside space-y-1">
                  {erros.map((erro, index) => (
                    <li key={index}>{erro}</li>
                  ))}
                </ul>
              </AlertDescription>
            </Alert>
          )}

          {/* Botões */}
          <div className="flex gap-3 pt-6 mt-6 border-t sticky bottom-0 bg-white">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={loading}
              className="flex-1"
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={loading}
              className="flex-1 bg-green-600 hover:bg-green-700"
            >
              {loading ? (
                'Processando...'
              ) : (
                <>
                  Confirmar Reembolso
                  <ArrowRight className="w-4 h-4 ml-2" />
                </>
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default ModalReembolsoPix; 